import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Hammer } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MotionLink = motion.create(Link);

// Disabled project icon — grayed out, not clickable, no name (still in development).
const DisabledIcon = ({ p }) => (
  <motion.div
    variants={fadeUp}
    data-testid="portfolio-project-disabled"
    aria-disabled="true"
    title="Noch in Entwicklung"
    className="flex w-24 cursor-not-allowed flex-col items-center gap-3 sm:w-28"
  >
    <div
      className="relative h-20 w-20 overflow-hidden rounded-[22px] opacity-40 grayscale sm:h-24 sm:w-24 sm:rounded-[26px]"
      style={{
        boxShadow: "0 10px 26px rgba(0,0,0,0.35)",
        border: "1px solid rgba(214,255,226,0.10)",
      }}
    >
      <img
        src={p.icon}
        alt=""
        aria-hidden="true"
        className="h-full w-full object-cover"
        draggable={false}
      />
      <span
        className="pointer-events-none absolute inset-0"
        style={{ background: "rgba(7,10,8,0.35)" }}
      />
    </div>
    <span
      data-testid="portfolio-coming-soon-badge"
      className="whitespace-nowrap rounded-full border px-2.5 py-1 font-mono-lx text-[10px] uppercase tracking-[0.1em]"
      style={{
        borderColor: "var(--lx-border)",
        color: "var(--lx-muted)",
        background: "var(--lx-surface-2)",
      }}
    >
      Bald verfügbar
    </span>
  </motion.div>
);

// iOS-style app icon (squircle) for the released app — links to the detail page.
const AppIcon = ({ p }) => (
  <MotionLink
    to={`/projekte/${p.id}`}
    variants={fadeUp}
    data-testid="portfolio-project-card"
    className="group flex w-24 flex-col items-center gap-3 sm:w-28"
    aria-label={`${p.name} – Details ansehen`}
  >
    <div
      className="relative h-20 w-20 overflow-hidden rounded-[22px] transition-transform duration-300 group-hover:scale-105 group-active:scale-95 sm:h-24 sm:w-24 sm:rounded-[26px]"
      style={{
        boxShadow:
          "0 10px 26px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
        border: "1px solid rgba(214,255,226,0.14)",
      }}
    >
      <img
        src={p.icon}
        alt={`${p.name} App-Icon`}
        className="h-full w-full object-cover"
      />
      {/* Glossy highlight */}
      <span
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.25), rgba(255,255,255,0) 55%)",
        }}
      />
    </div>
    <span className="text-center text-[13px] leading-tight text-[color:var(--lx-text)] transition-colors group-hover:text-[color:var(--lx-green-soft)]">
      {p.name}
    </span>
  </MotionLink>
);

// "App in development" icon with a hammer — links to contact.
const BuildingIcon = () => (
  <MotionLink
    to="/#kontakt"
    variants={fadeUp}
    data-testid="portfolio-coming-soon"
    className="group flex w-24 flex-col items-center gap-3 sm:w-28"
    aria-label="Neue App in Entwicklung – Projekt anfragen"
  >
    <div
      className="relative grid h-20 w-20 place-items-center rounded-[22px] border transition-transform duration-300 group-hover:scale-105 group-active:scale-95 sm:h-24 sm:w-24 sm:rounded-[26px]"
      style={{
        background:
          "repeating-linear-gradient(45deg, rgba(168,232,92,0.06) 0px, rgba(168,232,92,0.06) 8px, rgba(14,20,16,0.9) 8px, rgba(14,20,16,0.9) 16px)",
        borderColor: "rgba(168,232,92,0.35)",
        borderStyle: "dashed",
      }}
    >
      <motion.span
        animate={{ rotate: [0, -22, 6, 0] }}
        transition={{
          duration: 1.4,
          repeat: Infinity,
          repeatDelay: 0.6,
          ease: "easeInOut",
        }}
        style={{ transformOrigin: "70% 70%", display: "inline-flex" }}
      >
        <Hammer size={34} strokeWidth={1.9} style={{ color: "var(--lx-green)" }} />
      </motion.span>
    </div>
    <span className="text-center text-[13px] leading-tight text-[color:var(--lx-text-2)] transition-colors group-hover:text-[color:var(--lx-green-soft)]">
      In Entwicklung
    </span>
  </MotionLink>
);

export const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/projects`)
      .then((res) => {
        if (active) setProjects(res.data.projects || []);
      })
      .catch((e) => console.error("Failed to load projects", e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <section
      id="projekte"
      data-testid="portfolio-section"
      className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24"
    >
      <div className="mb-12 max-w-2xl">
        <div className="lx-kicker mb-3">Referenzen</div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-2xl lg:text-3xl">
          Unsere Produkte.
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-[color:var(--lx-text-2)] sm:text-base">
          Apps, Web-Apps und Plattformen. Tippe auf ein Icon für die Details.
          Qualität vor Quantität, und das nächste Produkt entsteht gerade.
        </p>
      </div>

      {loading ? (
        <div className="flex gap-8">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-3">
              <div className="h-20 w-20 animate-pulse rounded-[22px] sm:h-24 sm:w-24" style={{ background: "var(--lx-surface-2)" }} />
              <div className="h-3 w-16 animate-pulse rounded" style={{ background: "var(--lx-surface-2)" }} />
            </div>
          ))}
        </div>
      ) : (
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          data-testid="portfolio-grid"
          className="flex flex-wrap items-start gap-x-8 gap-y-10 sm:gap-x-12"
        >
          {projects.map((p) =>
            p.disabled ? (
              <DisabledIcon key={p.id} p={p} />
            ) : (
              <AppIcon key={p.id} p={p} />
            )
          )}
          <BuildingIcon />
        </motion.div>
      )}
    </section>
  );
};
